import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Grid, Modal } from '@mui/material';

import FilterSelectionModal from './FilterSelectionModal';
import { transformFilterVals } from './helper';
import { callApi } from 'src/core/callApi';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';
import { OnboardingModalMobile } from '../../Modals';
import NameConfirmationModal from './NameConfirmationModal';

const SubmarketCreationModal = ({
  open,
  setOpen,
  matches,
  getTokenThunk,
  tailoredRegionMarkers,
  getTailoredRegionMarkers,
  handleOnSampleClick,
  allListings,
  filterVal,
  setFilterVal,
  currency,
  revPotentialRangeInvestment,
  setIndividualRevenuePotentialRange,
  priceTiers,
  submarketPolygonPaths,
  setSubmarketPolygonPaths,
  boundarySelectionModalOpen,
  setBoundarySelectionModalOpen,
  submarketCreationSource,
  parentMarketID,
}) => {
  const search = useSelector(state => (state as any).dashboard.search);

  const [submarketName, setSubmarketName] = useState('');
  const [customSubmarketName, setCustomSubmarketName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (submarketCreationSource === 'map') {
      const defaultSubmarketName = `A neighborhood in ${search.data.name}`;
      setSubmarketName(defaultSubmarketName);
    }
  }, [search.data, submarketCreationSource]);

  const handleModalOnClose = () => {
    setSubmarketPolygonPaths([]);
    setBoundarySelectionModalOpen(true);
    setOpen(false);
  };

  const onSubmit = async () => {
    const filters = transformFilterVals(filterVal);
    console.log('Params: ', submarketPolygonPaths, filters, submarketName);

    setLoading(true);
    setErrorMessage(null);
    setSuccess(false);

    const url = 'https://3jg8y4cgz1.execute-api.us-east-2.amazonaws.com/default/handleSubmarket';

    const requestBody = {
      market_id: search.data.id,
      name: submarketName,
      polygon_paths: submarketPolygonPaths,
      ...filters,
    };

    try {
      const data = await callApi(url, { body: requestBody }, true, false, '', 'POST', true);

      if (data.error) {
        setErrorMessage(data.error);
      } else {
        setSuccess(true);
        const submarketID = data.message.id;
        const submarketURL = `${window.location.origin}/airbnb-data/submarket/${submarketID}`;
        setTimeout(() => {
          window.open(submarketURL, '_blank');
          handleModalOnClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error handling submarket creation:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSubmarket = async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccess(false);

    const url = 'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

    let polygonString = '';
    submarketPolygonPaths.forEach((polygon, i) => {
      polygonString += `(${polygon.toString().replace(',', ' ')}),`;
    });
    polygonString = polygonString.slice(0, -1);

    const requestConfig = {
      queryParams: {
        countryCode: search.data.countryCode,
        polygon: polygonString,
        parentMarketID: parentMarketID ? parentMarketID : search.data.id,
      },
    };

    try {
      const data = await callApi(url, requestConfig, true);

      if (data.error) {
        setErrorMessage(data.error);
      } else {
        setSuccess(true);
        const submarketName = data.message.district;
        setSubmarketName(submarketName);
      }
    } catch (error) {
      console.error('Error handling submarket creation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBoundarySelectionModalClose = async () => {
    if (submarketCreationSource === 'map') {
      setBoundarySelectionModalOpen(false);
    } else {
      await createSubmarket();
      setBoundarySelectionModalOpen(false);
    }
  };

  const redirectToSubmarket = () => {
    const submarketURL = `${window.location.origin}/airbnb-data/0/0/${submarketName}`;
    setTimeout(() => {
      window.open(submarketURL, '_blank');
      handleModalOnClose();
    }, 2000);
  };

  return (
    <>
      <Modal open={open} onClose={handleModalOnClose}>
        <Grid
          container
          direction="column"
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            outline: 'none !important',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            pt: 2,
            px: 4,
            pb: 3,
            overflowY: 'scroll',
          }}
        >
          {boundarySelectionModalOpen ? (
            <OnboardingModalMobile
              getTokenThunk={getTokenThunk}
              setIsTailoredRegionOpen={setOpen}
              tailoredRegionMarkers={tailoredRegionMarkers}
              getTailoredRegionMarkers={getTailoredRegionMarkers}
              handleOnSampleClick={handleOnSampleClick}
              title={`Define Submarket in ${search.data.name}`}
              center={{ lat: search.data.lat, lng: search.data.lng }}
              zoom={12}
              showMarketPolygon={true}
              setSubmarketPolygonPaths={setSubmarketPolygonPaths}
              confirmationButtonText="Set Submarket"
              handleConfirmation={handleBoundarySelectionModalClose}
              hideListingsCount={true}
              alertText={`Draw a submarket inside ${search.data.name}. This will create a new submarket dashboard. Click the "Draw Your Submarket" button on the bottom center of the map to get started.`}
              loading={loading}
            />
          ) : submarketCreationSource === 'map' ? (
            <FilterSelectionModal
              handleClose={handleModalOnClose}
              center={{ lat: search.data.lat, lng: search.data.lng }}
              zoom={12}
              submarketPolygonPaths={submarketPolygonPaths}
              setBoundarySelectionModalOpen={setBoundarySelectionModalOpen}
              matches={matches}
              allListings={allListings}
              filterVal={filterVal}
              setFilterVal={setFilterVal}
              currency={currency}
              revPotentialRangeInvestment={revPotentialRangeInvestment}
              setIndividualRevenuePotentialRange={setIndividualRevenuePotentialRange}
              priceTiers={priceTiers}
              handleOnSampleClick={handleOnSampleClick}
              onSubmit={onSubmit}
              submarketName={submarketName}
              setSubmarketName={setSubmarketName}
              loading={loading}
            />
          ) : (
            <NameConfirmationModal
              open={true}
              onClose={() => {
                redirectToSubmarket();
                setOpen(false);
              }}
              onConfirm={() => {
                redirectToSubmarket();
                setOpen(false);
              }}
              submarketName={submarketName}
              setSubmarketName={setSubmarketName}
            />
          )}
        </Grid>
      </Modal>

      {errorMessage && <AutohideSnackbar message={errorMessage} type="error" />}
      {success && (
        <AutohideSnackbar
          message={
            ' Submarket created! Please ensure your browser allows pop-ups to view the new window.'
          }
          type="success"
        />
      )}
    </>
  );
};

export default SubmarketCreationModal;
